import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, CreditCard, Bitcoin, Copy, Loader2 } from "lucide-react";

// Vercel build trigger v2
export default function ProductDetail() {
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [cryptoSession, setCryptoSession] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    API.get(`/products/${productId}`).then(r => setProduct(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [productId]);

  const handlePurchaseClick = () => {
    if (!user) { navigate("/login"); return; }
    setShowModal(true);
  };

  const handlePaytrCheckout = async () => {
    try {
      const { data } = await API.post("/checkout/paytr/create", { product_id: productId, origin_url: window.location.origin });
      window.location.href = data.iframe_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  };

  const handleCryptoCheckout = async () => {
    try {
      const { data } = await API.post("/checkout/crypto/create", { product_id: productId });
      setCryptoSession(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Crypto checkout failed");
    }
  };

  const verifyCryptoPayment = async () => {
    if (!cryptoSession) return;
    setVerifying(true);
    try {
      const { data } = await API.get(`/checkout/crypto/verify/${cryptoSession.session_id}`);
      if (data.status === "paid") {
        toast.success("Payment verified successfully!");
        navigate(`/payment/success?session_id=${cryptoSession.session_id}`);
      } else {
        toast.info("Payment not found yet. It might take a few minutes for the network to confirm.");
      }
    } catch (e) {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <div className="w-6 h-6 border-2 border-[#00FF41] border-t-transparent animate-spin" />
    </div>
  );

  if (!product) return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0] flex items-center justify-center">
      <div className="text-center">
        <p className="text-lg font-medium mb-4">Product not found</p>
        <Link to="/" className="text-[#00FF41] text-sm">Back to Home</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="product-detail-page">
      <nav className="border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase">
            NRD<span className="text-[#00FF41]">.</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#00FF41] transition-colors" data-testid="product-back-home">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-16">
          <div className="aspect-[4/3] overflow-hidden border border-[#1E1E1E] relative">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 via-transparent to-transparent" />
          </div>

          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-2">{product.tagline}</p>
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-4" data-testid="product-name">{product.name}</h1>
            <p className="text-base text-slate-400 leading-relaxed mb-8">{product.description}</p>

            <div className="mb-8">
              <span className="font-heading text-5xl font-black text-[#00FF41]">${product.price}</span>
              <span className="text-sm text-slate-500">/month</span>
            </div>

            <button
              onClick={handlePurchaseClick}
              className="inline-flex items-center gap-2 bg-[#00FF41] text-[#0A0A0A] px-8 py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors mb-8 animate-glow"
              data-testid="product-purchase-button"
            >
              Subscribe Now <ArrowRight size={16} />
            </button>

            <div className="border-t border-[#1E1E1E] pt-8">
              <h3 className="font-heading text-lg font-medium tracking-tight mb-4">What's Included</h3>
              <ul className="space-y-3">
                {product.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check size={16} className="text-[#00FF41] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#0A0A0A] border border-[#1E1E1E] p-8 max-w-md w-full relative">
            <button onClick={() => { setShowModal(false); setCryptoSession(null); }} className="absolute top-4 right-4 text-slate-500 hover:text-white">✕</button>
            <h2 className="font-heading text-2xl font-bold mb-6">Select Payment Method</h2>
            
            {!cryptoSession ? (
              <div className="space-y-4">
                <button onClick={handlePaytrCheckout} className="w-full flex items-center justify-between bg-[#1E1E1E] hover:bg-[#2A2A2A] p-4 transition-colors">
                  <span className="flex items-center gap-3"><CreditCard size={20} className="text-[#00FF41]" /> Credit Card</span>
                  <ArrowRight size={16} className="text-slate-500" />
                </button>
                <button onClick={handleCryptoCheckout} className="w-full flex items-center justify-between bg-[#1E1E1E] hover:bg-[#2A2A2A] p-4 transition-colors">
                  <span className="flex items-center gap-3"><Bitcoin size={20} className="text-[#00FF41]" /> Crypto (USDT)</span>
                  <ArrowRight size={16} className="text-slate-500" />
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 text-sm text-yellow-200">
                  <p className="font-bold mb-1">Important:</p>
                  <p>Send EXACTLY <strong>{cryptoSession.amount}</strong> {cryptoSession.currency} to the address below via the <strong>{cryptoSession.network}</strong> network.</p>
                </div>
                
                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-widest mb-2">Amount to send</label>
                  <div className="flex items-center justify-between bg-[#111] border border-[#1E1E1E] p-3">
                    <span className="font-mono text-[#00FF41] font-bold text-lg">{cryptoSession.amount} {cryptoSession.currency}</span>
                    <button onClick={() => copyToClipboard(cryptoSession.amount)} className="text-slate-400 hover:text-white"><Copy size={16}/></button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 uppercase tracking-widest mb-2">Wallet Address ({cryptoSession.network})</label>
                  <div className="flex items-center justify-between bg-[#111] border border-[#1E1E1E] p-3 break-all">
                    <span className="font-mono text-sm text-slate-300">{cryptoSession.wallet_address}</span>
                    <button onClick={() => copyToClipboard(cryptoSession.wallet_address)} className="text-slate-400 hover:text-white ml-3 shrink-0"><Copy size={16}/></button>
                  </div>
                </div>

                <button 
                  onClick={verifyCryptoPayment} 
                  disabled={verifying}
                  className="w-full flex items-center justify-center gap-2 bg-[#00FF41] text-[#0A0A0A] py-3 font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
                >
                  {verifying ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  I Have Paid (Verify)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
